import React from 'react';

import { useScreenSize } from '@/hooks/useScreenSize';

import { Notification } from './Notification';
import { useNotification } from './useNotification';

const getDismissedNotificationIds = () => {
  try {
    const stored = localStorage.getItem('dismissedNotifs');
    if (!stored) return [];

    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed.filter(id => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

interface Props {
  notifAgent: string;
  isSignedIn?: boolean;
  domain: string;
  specialHandler?: string;
  notificationRef?: any;
  setNotificationHeight?: (value: number) => void;
}

export const NotificationManager = ({
  notifAgent,
  isSignedIn = false,
  domain,
  specialHandler,
  notificationRef,
  setNotificationHeight,
}: Props) => {
  let { notifications, dismissNotification, engageNotification } = useNotification({
    notifAgent,
    isSignedIn,
    domain,
    specialHandler,
  });
  const { getSize } = useScreenSize();
  const [dismissedIds, setDismissedIds] = React.useState<string[]>(getDismissedNotificationIds);

  const filteredNotifications = notifications.filter(notif => !dismissedIds.includes(notif.id));

  React.useLayoutEffect(() => {
    if (!filteredNotifications || filteredNotifications.length === 0) {
      setNotificationHeight?.(0);
      return;
    }
    if (!notificationRef?.current) return;

    const containerHeight = notificationRef.current.offsetHeight;
    setNotificationHeight?.(containerHeight);
  }, [filteredNotifications, notificationRef, setNotificationHeight]);

  const handleDismissAll = () => {
    const notificationIds = filteredNotifications.map(notif => notif.id);
    notificationIds.forEach(notifId => dismissNotification(notifId));

    const newDismissed = Array.from(new Set([...dismissedIds, ...notificationIds]));
    setDismissedIds(newDismissed);
    localStorage.setItem('dismissedNotifs', JSON.stringify(newDismissed));
    setNotificationHeight?.(0);
  };

  if (filteredNotifications.length === 0) return null;

  return (
    <div
      className="fixed top-0 z-[50] w-full"
      id="notification-banner"
      data-testid="test-notification-banner"
      ref={notificationRef}
    >
      {filteredNotifications.map((notif: any, index: number) => (
        <div key={notif.id} className="pointer-events-auto">
          <Notification
            size={getSize}
            notification={notif}
            dismissAction={handleDismissAll}
            engageAction={engageNotification}
            notificationIndexId={index}
          />
        </div>
      ))}
    </div>
  );
};
